import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import AppLayout from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import AssetsPage from "@/pages/AssetsPage";
import DepreciationPage from "@/pages/DepreciationPage";
import JournalsPage from "@/pages/JournalsPage";
import DisposalsPage from "@/pages/DisposalsPage";
import FarPage from "@/pages/FarPage";
import CapexPage from "@/pages/CapexPage";
import PoliciesPage from "@/pages/PoliciesPage";
import ReassessmentsPage from "@/pages/ReassessmentsPage";
import SuggestionsPage from "@/pages/SuggestionsPage";
import UsersPage from "@/pages/UsersPage";
import SettingsPage from "@/pages/SettingsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ImportPage from "@/pages/ImportPage";
import TaxDepPage from "@/pages/TaxDepPage";
import AnomaliesPage from "@/pages/AnomaliesPage";
import HealthScorePage from "@/pages/HealthScorePage";
import PolicyDocPage from "@/pages/PolicyDocPage";
import OnboardingPage from "@/pages/OnboardingPage";
import BoardPackPage from "@/pages/BoardPackPage";
import IndustryModulesPage from "@/pages/IndustryModulesPage";
import AuditorPackPage from "@/pages/AuditorPackPage";
import LeasesPage from "@/pages/LeasesPage";
import ImpairmentPage from "@/pages/ImpairmentPage";
import ReclassPage from "@/pages/ReclassPage";
import LandingPage from "@/pages/LandingPage";
import GuidePage from "@/pages/GuidePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SupportPage from "@/pages/SupportPage";
import SecurityPage from "@/pages/SecurityPage";
import ScenarioPage from "@/pages/ScenarioPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F5EF] flex items-center justify-center">
        <div className="text-center">
          <img src="/assets/stratum_logomark.png" alt="Stratum" className="w-16 h-16 mx-auto mb-4 rounded-xl animate-pulse" />
          <p className="text-[#6B7280] text-sm">Loading Stratum...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppProvider>
      <AppLayout>{children}</AppLayout>
    </AppProvider>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public: Landing page for unauthenticated visitors */}
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          {/* App: Authenticated routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute><AssetsPage /></ProtectedRoute>} />
          <Route path="/depreciation" element={<ProtectedRoute><DepreciationPage /></ProtectedRoute>} />
          <Route path="/journals" element={<ProtectedRoute><JournalsPage /></ProtectedRoute>} />
          <Route path="/disposals" element={<ProtectedRoute><DisposalsPage /></ProtectedRoute>} />
          <Route path="/far" element={<ProtectedRoute><FarPage /></ProtectedRoute>} />
          <Route path="/capex" element={<ProtectedRoute><CapexPage /></ProtectedRoute>} />
          <Route path="/policies" element={<ProtectedRoute><PoliciesPage /></ProtectedRoute>} />
          <Route path="/reassessments" element={<ProtectedRoute><ReassessmentsPage /></ProtectedRoute>} />
          <Route path="/suggestions" element={<ProtectedRoute><SuggestionsPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
          <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
          <Route path="/tax-depreciation" element={<ProtectedRoute><TaxDepPage /></ProtectedRoute>} />
          <Route path="/anomalies" element={<ProtectedRoute><AnomaliesPage /></ProtectedRoute>} />
          <Route path="/health-score" element={<ProtectedRoute><HealthScorePage /></ProtectedRoute>} />
          <Route path="/policy-document" element={<ProtectedRoute><PolicyDocPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/board-pack" element={<ProtectedRoute><BoardPackPage /></ProtectedRoute>} />
          <Route path="/industry-modules" element={<ProtectedRoute><IndustryModulesPage /></ProtectedRoute>} />
          <Route path="/auditor-pack" element={<ProtectedRoute><AuditorPackPage /></ProtectedRoute>} />
          <Route path="/leases" element={<ProtectedRoute><LeasesPage /></ProtectedRoute>} />
          <Route path="/impairment" element={<ProtectedRoute><ImpairmentPage /></ProtectedRoute>} />
          <Route path="/reclassification" element={<ProtectedRoute><ReclassPage /></ProtectedRoute>} />
          <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
          <Route path="/scenarios" element={<ProtectedRoute><ScenarioPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
